import { useAuth } from '@/context/AuthContext';
import { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import * as Sentry from '@sentry/react';

const years = [2026, 2025, 2024];
const months = [
    { value: 1, label: "January" },
    { value: 2, label: "February" },
    { value: 3, label: "March" },
    { value: 4, label: "April" },
    { value: 5, label: "May" },
    { value: 6, label: "June" },
    { value: 7, label: "July" },
    { value: 8, label: "August" },
    { value: 9, label: "September" },
    { value: 10, label: "October" },
    { value: 11, label: "November" },
    { value: 12, label: "December" }
];

const OtherLegalMatters = () => {
    const { apiFetch } = useAuth();
    const [allData, setAllData] = useState([]);
    const [selectedYear, setSelectedYear] = useState(years[0]);
    const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
    const [otherLegalMattersCount, setOtherLegalMattersCount] = useState('');
    const [legalArrearsMattersCount, setLegalArrearsMattersCount] = useState('');
    const [saving, setSaving] = useState(false);

    const fetchOtherLegalMatters = useCallback(async () => {
        try {
            const response = await apiFetch('/api/legal/OtherLegalMatters');
            const data = await response.json();

            if (data.success && Array.isArray(data.data)) {
                setAllData(data.data);
            }
        } catch (e) {
            Sentry.captureException(e);
            console.error(e);
        }
    }, [apiFetch]);

    useEffect(() => {
        fetchOtherLegalMatters();
    }, [fetchOtherLegalMatters]);

    useEffect(() => {
        const period = Number(`${selectedYear}${selectedMonth.toString().padStart(2, '0')}`);
        const found = allData.find(d => d.period === period);
        setOtherLegalMattersCount(found ? found.otherLegalMattersCount : '');
        setLegalArrearsMattersCount(found ? found.legalArrearsMattersCount : '');
    }, [selectedYear, selectedMonth, allData]);

    const handleYearChange = (e) => {
        setSelectedYear(Number(e.target.value));
    };

    const handleMonthChange = (e) => {
        setSelectedMonth(Number(e.target.value));
    };

    const handleNumericChange = (setter) => (e) => {
        const val = e.target.value;
        if (/^\d*$/.test(val)) setter(val);
    };

    const handleSave = async () => {
        setSaving(true);
        const period = Number(`${selectedYear}${selectedMonth.toString().padStart(2, '0')}`);
        try {
            const response = await apiFetch('/api/legal/OtherLegalMatters', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    period,
                    otherLegalMattersCount: Number(otherLegalMattersCount) || 0,
                    legalArrearsMattersCount: Number(legalArrearsMattersCount) || 0
                })
            });
            const data = await response.json();
            if (data.success) {
                fetchOtherLegalMatters();
                toast.success("Other Legal Matters saved successfully");
            } else {
                toast.error("An error occurred while saving data");
            }
        } catch (e) {
            Sentry.captureException(e);
            toast.error("Save failed.");
            console.error(e);
        }
        setSaving(false);
    };

    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth() + 1;
    const getMonthDisabled = (month) => {
        if (selectedYear < currentYear) return false;
        if (selectedYear > currentYear) return true;
        return month > currentMonth;
    };

    const canSave = otherLegalMattersCount !== '' || legalArrearsMattersCount !== '';

    return (
        <div>
            <h1>Enter Other Legal Matters</h1>
            <div style={{ marginBottom: 16 }}>
                <label>
                    Year:&nbsp;
                    <select value={selectedYear} onChange={handleYearChange}>
                        {years.map(y => (
                            <option key={y} value={y}>{y}</option>
                        ))}
                    </select>
                </label>
                &nbsp;&nbsp;
                <label>
                    Month:&nbsp;
                    <select value={selectedMonth} onChange={handleMonthChange}>
                        {months.map(m => (
                            <option
                                key={m.value}
                                value={m.value}
                                disabled={getMonthDisabled(m.value)}
                            >
                                {m.label}
                            </option>
                        ))}
                    </select>
                </label>
            </div>
            <div style={{ marginBottom: 16 }}>
                <label>
                    Number of Other Legal Matters:&nbsp;
                    <input
                        type="number"
                        min="0"
                        value={otherLegalMattersCount}
                        onChange={handleNumericChange(setOtherLegalMattersCount)}
                        style={{ width: 80 }}
                    />
                </label>
            </div>
            <div style={{ marginBottom: 16 }}>
                <label>
                    Legal Arrears Matters:&nbsp;
                    <input
                        type="number"
                        min="0"
                        value={legalArrearsMattersCount}
                        onChange={handleNumericChange(setLegalArrearsMattersCount)}
                        style={{ width: 80 }}
                    />
                </label>
            </div>
            <button onClick={handleSave} disabled={saving || !canSave}>
                {saving ? "Saving..." : "Save"}
            </button>
        </div>
    );
};

export default OtherLegalMatters;